// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { useRouter } from 'expo-router';

import type { UserSession } from '@/app/user-context';
import { buildAuthUrl, exchangeCode, parseAuthCallback, REDIRECT_URI } from '@/auth/oidc';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';

WebBrowser.maybeCompleteAuthSession();

type UseAuthRedirectParams = {
  user: UserSession | null;
  setUser: (user: UserSession | null) => void;
  onError?: (message: string) => void;
  onSuccess?: () => void;
  debug?: boolean;
  returnTo?: string | null;
};

const buildErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const POST_LOGIN_PATH_KEY = 'goffy:post-login-path';

export const useAuthRedirect = ({ user, setUser, onError, onSuccess, debug, returnTo }: UseAuthRedirectParams) => {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'auth-redirect',
      }),
    [apiBaseUrl, user?.token]
  );

  const handleAuthSuccess = useCallback(
    (auth: { id: string; name: string; accessToken: string; idToken?: string | null; refreshToken?: string | null }) => {
      setUser({
        id: auth.id,
        forename: auth.name,
        token: auth.accessToken,
        idToken: auth.idToken ?? null,
        refreshToken: auth.refreshToken ?? null,
      });
      const storedReturnTo =
        typeof window !== 'undefined' ? window.sessionStorage.getItem(POST_LOGIN_PATH_KEY) : null;
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
      }
      const nextRoute = storedReturnTo || `/tournaments${debug ? '?debug=true' : ''}`;
      router.replace(nextRoute);
      onSuccess?.();
    },
    [debug, onSuccess, router, setUser]
  );

  const handleRedirect = useCallback(async () => {
    logToBff('login.redirect', { hasUser: !!user }, 'info');
    try {
      setLoading(true);
      if (typeof window !== 'undefined') {
        if (returnTo) {
          window.sessionStorage.setItem(POST_LOGIN_PATH_KEY, returnTo);
        } else {
          window.sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
        }
      }
      const url = await buildAuthUrl();
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
        window.location.href = url;
        return;
      }
      const result = await WebBrowser.openAuthSessionAsync(url, REDIRECT_URI);
      if (result.type === 'success' && result.url) {
        const { code, state } = parseAuthCallback(result.url);
        if (!code) {
          onError?.('Inloggningen kunde inte slutföras.');
          return;
        }
        const auth = await exchangeCode(code, state ?? undefined);
        handleAuthSuccess(auth);
      } else if (result.type === 'dismiss') {
        onError?.('Inloggningen avbröts.');
      }
    } catch (err) {
      onError?.(buildErrorMessage(err, 'Kunde inte starta inloggning.'));
    } finally {
      setLoading(false);
    }
  }, [handleAuthSuccess, logToBff, onError, returnTo, user]);

  return {
    loading,
    handleRedirect,
  };
};
